import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from 'lenis';

// Register plugins once
gsap.registerPlugin(ScrollTrigger);

let lenis: Lenis | null = null;
let isInitialized = false;
let rafCallback: ((time: number) => void) | null = null;

// Extend Window interface for global refresh method
declare global {
  interface Window {
    refreshScrollTriggers?: () => void;
  }
}

/**
 * Optimized GSAP and smooth scroll initialization
 * Combines GSAP setup with Lenis smooth scrolling for better performance
 */
export function initGSAP() {
  // Prevent multiple initializations
  if (isInitialized) return;

  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  // Initialize Lenis smooth scroll
  lenis = new Lenis({
    touchMultiplier: isMobile ? 1.3 : 1,
    smoothWheel: true,
    duration: isMobile ? 0.8 : 1,
    lerp: isMobile ? 0.1 : 1,
    orientation: 'vertical',
    infinite: false,
    syncTouch: true, // Better touch device handling
  });

  // Optimize Lenis-GSAP integration
  lenis.on('scroll', ScrollTrigger.update);

  // Use more efficient RAF integration
  rafCallback = (time: number) => {
    if (lenis) {
      lenis.raf(time * 1000);
    }
  };
  gsap.ticker.add(rafCallback);

  // Disable lag smoothing for better performance
  gsap.ticker.lagSmoothing(0);

  // Global refresh handler (use sparingly)
  const refreshScrollTriggers = () => {
    ScrollTrigger.refresh();
  };

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    lenis?.destroy();
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    if (rafCallback) {
      gsap.ticker.remove(rafCallback);
    }
  });

  window.addEventListener('resize', () => {
    refreshScrollTriggers();
  });

  // Expose refresh method for specific use cases
  window.refreshScrollTriggers = refreshScrollTriggers;

  isInitialized = true;
}

/**
 * Utility for creating optimized ScrollTrigger animations
 */
export function createScrollTrigger(element: HTMLElement, config: gsap.TweenVars & { scrollTrigger: ScrollTrigger.Vars }) {
  // Add performance optimizations
  const scrollTriggerConfig = config.scrollTrigger as ScrollTrigger.Vars;
  const optimizedConfig = {
    ...config,
    scrollTrigger: {
      ...scrollTriggerConfig,
      // Add default optimizations
      invalidateOnRefresh: scrollTriggerConfig.invalidateOnRefresh ?? true,
      fastScrollEnd: scrollTriggerConfig.fastScrollEnd ?? true,
    }
  };

  return gsap.to(element, optimizedConfig);
}

/**
 * Utility for killing triggers efficiently
 */
export function killTriggersForElement(element: HTMLElement) {
  ScrollTrigger.getAll().forEach((trigger) => {
    if (trigger.vars.trigger === element || trigger.trigger === element) {
      trigger.kill();
    }
  });
}

export function resetSlide(slide: HTMLElement) {
  // Kill any existing tweens on elements inside this slide
  gsap.killTweensOf(slide.querySelectorAll("*"));

  // Reset inline styles applied by GSAP (opacity, transform, etc.)
  gsap.set(slide.querySelectorAll("*"), { clearProps: "all" });
}

export { gsap, ScrollTrigger, lenis };